import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, Phone, Lock, User, MapPin, Building, ArrowLeft,
  Package, CheckCircle, ShoppingBag, Bell, Home, Shirt,
  ArrowRight, Calendar, Zap, Star, Mail, ShieldCheck, RefreshCw
} from 'lucide-react';

// ─── CUSTOMER TYPE CHOICE SCREEN ──────────────────────────────────────────────
const CustomerTypeChoice = ({ onSelectPG, onSelectIndividual, onBack }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-violet-50 flex flex-col items-center justify-center px-6 py-12" style={{ fontFamily: 'Inter, sans-serif' }}>
    <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(circle, #CBD5E1 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
    <div className="relative z-10 w-full max-w-md">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 text-sm mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </button>
      <div className="text-center mb-8">
        <img src="/logo.png" alt="JustFold" className="h-10 mx-auto mb-6 object-contain" />
        <h1 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Book Now</h1>
        <p className="text-slate-500 text-sm">How are you using JustFold?</p>
      </div>
      <div className="space-y-4">
        <button onClick={onSelectPG}
          className="w-full group bg-white border-2 border-slate-200 hover:border-blue-400 rounded-3xl p-6 text-left transition-all hover:shadow-lg hover:shadow-blue-100 hover:-translate-y-0.5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 group-hover:bg-blue-600 rounded-2xl flex items-center justify-center transition-colors flex-shrink-0">
              <Building className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-slate-900 mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>PG / Co-Living</h3>
              <p className="text-sm text-slate-400">I live in a PG or co-living space</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {['Scheduled pickups', 'Cycle-based plan', 'PG residents'].map(tag => (
              <span key={tag} className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        </button>

        <button onClick={onSelectIndividual}
          className="w-full group bg-white border-2 border-slate-200 hover:border-orange-400 rounded-3xl p-6 text-left transition-all hover:shadow-lg hover:shadow-orange-100 hover:-translate-y-0.5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-100 group-hover:bg-orange-500 rounded-2xl flex items-center justify-center transition-colors flex-shrink-0">
              <Home className="w-7 h-7 text-orange-500 group-hover:text-white transition-colors" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-slate-900 mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Individual Customer</h3>
              <p className="text-sm text-slate-400">I have my own home or apartment</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-orange-400 transition-colors" />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {['Doorstep pickup', '72-hr delivery', 'Book anytime'].map(tag => (
              <span key={tag} className="text-[10px] font-bold bg-orange-50 text-orange-600 px-2.5 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        </button>
      </div>
      <p className="text-xs text-slate-300 text-center mt-6">JustFold — Premium laundry service in Gurgaon</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── CINEMATIC DEMO ────────────────────────────────────────────────────────

const SCENES = [
  {
    id: 1,
    chapter: 'Scene 01',
    title: 'Open JustFold',
    subtitle: 'Your laundry dashboard',
    glow: 'shadow-blue-200',
    ring: 'ring-blue-200',
    screen: 'dashboard',
  },
  {
    id: 2,
    chapter: 'Scene 02',
    title: 'Book a Pickup',
    subtitle: 'One tap to start',
    glow: 'shadow-orange-200',
    ring: 'ring-orange-200',
    screen: 'tap',
  },
  {
    id: 3,
    chapter: 'Scene 03',
    title: 'Fill Your Order',
    subtitle: 'Select plan & items',
    glow: 'shadow-violet-200',
    ring: 'ring-violet-200',
    screen: 'form',
  },
  {
    id: 4,
    chapter: 'Scene 04',
    title: 'Confirm Details',
    subtitle: 'Review your booking',
    glow: 'shadow-blue-200',
    ring: 'ring-blue-200',
    screen: 'confirm',
  },
  {
    id: 5,
    chapter: 'Scene 05',
    title: 'Order Placed!',
    subtitle: 'Pickup on its way',
    glow: 'shadow-emerald-200',
    ring: 'ring-emerald-200',
    screen: 'success',
  },
];

const SCENE_DURATION = 3800;
const TRANSITION_MS = 450;

// ── Phone Screens (light theme) ─────────────────────────────────────────────

const ScreenDashboard = () => (
  <div className="flex flex-col h-full bg-slate-50">
    {/* Status bar */}
    <div className="flex justify-between items-center px-4 pt-3 pb-1 bg-blue-600">
      <span className="text-[9px] text-blue-100">9:41</span>
      <div className="flex gap-0.5 items-end">
        {[3,4,4,3].map((h,i) => <div key={i} className="w-0.5 rounded-sm bg-white" style={{height:`${h}px`}} />)}
      </div>
    </div>
    {/* Header */}
    <div className="px-4 pt-3 pb-3 bg-blue-600">
      <p className="text-[9px] text-blue-200">Good morning 👋</p>
      <p className="text-sm font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Rahul Kumar</p>
    </div>
    {/* Plan card */}
    <div className="mx-3 mt-3 mb-2 bg-white rounded-2xl p-3 shadow-sm border border-blue-100">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[8px] text-blue-700 font-bold uppercase tracking-widest">Active Plan</p>
        <span className="text-[7px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">FoldPass</span>
      </div>
      <p className="text-xs font-bold text-slate-800">Next Pickup: Tuesday</p>
      <div className="mt-1.5 flex gap-1">
        {[1,1,1,0.3,0.15].map((o,i) => <div key={i} className="h-1 flex-1 rounded-full bg-blue-500" style={{opacity:o}} />)}
      </div>
      <p className="text-[8px] text-slate-400 mt-0.5">3 of 5 pickups used</p>
    </div>
    {/* Quick actions */}
    <div className="px-3 grid grid-cols-2 gap-2">
      <div className="bg-orange-500 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-md shadow-orange-200">
        <Package className="w-4 h-4 text-white" />
        <span className="text-[8px] font-bold text-white">New Order</span>
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-sm">
        <ShoppingBag className="w-4 h-4 text-slate-400" />
        <span className="text-[8px] text-slate-400">My Orders</span>
      </div>
    </div>
    {/* Bottom nav */}
    <div className="mt-auto border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <Home className="w-3.5 h-3.5 text-blue-600" />
      <ShoppingBag className="w-3.5 h-3.5 text-slate-300" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenTap = () => (
  <div className="flex flex-col h-full bg-slate-50">
    <div className="px-4 pt-3 pb-3 bg-blue-600">
      <p className="text-[9px] text-blue-200">Good morning 👋</p>
      <p className="text-sm font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Rahul Kumar</p>
    </div>
    <div className="mx-3 mt-3 mb-2 bg-white rounded-2xl p-3 shadow-sm border border-blue-100">
      <p className="text-[8px] text-blue-700 font-bold uppercase tracking-widest mb-1">Next Pickup</p>
      <p className="text-xs font-bold text-slate-800">Tuesday, 10 AM</p>
    </div>
    <div className="px-3 grid grid-cols-2 gap-2">
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl bg-orange-400/30 animate-ping scale-110" />
        <div className="relative bg-orange-500 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-lg shadow-orange-300 z-10">
          <Package className="w-4 h-4 text-white" />
          <span className="text-[8px] font-bold text-white">New Order</span>
        </div>
        {/* Tap cursor */}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md z-20 animate-bounce border border-orange-200">
          <div className="w-2 h-2 bg-orange-500 rounded-full" />
        </div>
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-sm">
        <ShoppingBag className="w-4 h-4 text-slate-400" />
        <span className="text-[8px] text-slate-400">My Orders</span>
      </div>
    </div>
    <div className="mt-auto border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <Home className="w-3.5 h-3.5 text-blue-600" />
      <ShoppingBag className="w-3.5 h-3.5 text-slate-300" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenForm = () => (
  <div className="flex flex-col h-full bg-white">
    <div className="bg-blue-600 px-4 pt-3 pb-3 flex items-center gap-2">
      <ArrowLeft className="w-3.5 h-3.5 text-blue-200" />
      <p className="text-xs font-bold text-white flex-1 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>New Order</p>
      <div className="w-3.5" />
    </div>
    <div className="px-3 mt-3 mb-2">
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {['FoldPass', 'FlexFold', 'Flash'].map((p, i) => (
          <div key={p} className={`flex-1 rounded-lg py-1.5 text-center text-[7.5px] font-bold ${i === 0 ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-400'}`}>{p}</div>
        ))}
      </div>
    </div>
    <div className="px-3 space-y-2 flex-1">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
        <p className="text-[8px] text-slate-400 mb-1.5 font-medium">Total Items</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shirt className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-sm font-black text-slate-800">8</span>
          </div>
          <div className="flex gap-1">
            <div className="w-6 h-6 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500 text-xs font-bold">-</div>
            <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">+</div>
          </div>
        </div>
      </div>
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5">
        <p className="text-[8px] text-slate-400 mb-1 font-medium">Pickup Date</p>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-orange-500" />
          <span className="text-[10px] font-bold text-slate-800">Tuesday, Mar 18</span>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
        <p className="text-[8px] text-slate-400 mb-1 font-medium">Note</p>
        <p className="text-[8px] text-slate-400">Delicate care for shirts...</p>
      </div>
      <button className="w-full bg-orange-500 rounded-xl py-2 text-[10px] font-bold text-white flex items-center justify-center gap-1 shadow-md shadow-orange-200">
        Continue <ArrowRight className="w-3 h-3" />
      </button>
    </div>
    <div className="border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <Home className="w-3.5 h-3.5 text-slate-300" />
      <ShoppingBag className="w-3.5 h-3.5 text-blue-600" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenConfirm = () => (
  <div className="flex flex-col h-full bg-white">
    <div className="bg-blue-600 px-4 pt-3 pb-3 flex items-center gap-2">
      <ArrowLeft className="w-3.5 h-3.5 text-blue-200" />
      <p className="text-xs font-bold text-white flex-1 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirm Order</p>
      <div className="w-3.5" />
    </div>
    <div className="px-3 mt-3 space-y-2 flex-1">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 space-y-2">
        {[
          { label: 'Plan', value: 'FoldPass', highlight: true },
          { label: 'Items', value: '8 clothes', highlight: false },
          { label: 'Pickup', value: 'Tue, 10 AM', highlight: false },
          { label: 'Delivery', value: 'Thu, 6 PM', highlight: false },
        ].map((row) => (
          <div key={row.label} className="flex justify-between items-center">
            <span className="text-[8px] text-slate-400 font-medium">{row.label}</span>
            <span className={`text-[8px] font-bold ${row.highlight ? 'text-blue-600' : 'text-slate-700'}`}>{row.value}</span>
          </div>
        ))}
        <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
          <span className="text-[8px] text-slate-500 font-semibold">Total</span>
          <span className="text-sm font-black text-orange-500">₹149</span>
        </div>
      </div>
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5 flex items-start gap-2">
        <MapPin className="w-3 h-3 text-orange-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[7.5px] font-bold text-orange-700">Pickup Address</p>
          <p className="text-[7px] text-slate-500 mt-0.5">Room 204, Sunrise PG, Patel Nagar</p>
        </div>
      </div>
      <button className="w-full bg-blue-600 rounded-xl py-2 text-[10px] font-bold text-white shadow-md shadow-blue-200">
        Place Order →
      </button>
    </div>
    <div className="border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <Home className="w-3.5 h-3.5 text-slate-300" />
      <ShoppingBag className="w-3.5 h-3.5 text-blue-600" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenSuccess = () => (
  <div className="flex flex-col h-full items-center justify-center px-4 pb-4 bg-white">
    <div className="relative mb-4">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="absolute rounded-full"
          style={{
            width: i%2===0?'4px':'5px', height: i%2===0?'4px':'5px',
            backgroundColor: i%3===0?'#2563EB':i%3===1?'#F97316':'#10B981',
            top:'50%', left:'50%',
            transform:`rotate(${i*45}deg) translateY(-34px) translateX(-50%)`,
          }} />
      ))}
      <div className="w-14 h-14 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center relative z-10">
        <CheckCircle className="w-7 h-7 text-emerald-500" />
      </div>
    </div>
    <p className="text-sm font-black text-slate-900 text-center mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Order Placed! 🎉</p>
    <p className="text-[8px] text-slate-400 text-center mb-3 leading-relaxed">Pickup on Tuesday at 10 AM<br />Delivery by Thursday</p>
    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 w-full text-center mb-2">
      <p className="text-[7px] text-slate-400 mb-0.5 font-medium">Order ID</p>
      <p className="text-[10px] font-mono font-bold text-blue-600">#JF-2026-0842</p>
    </div>
    <div className="flex gap-2 w-full">
      <button className="flex-1 bg-blue-600 rounded-lg py-1.5 text-[8px] font-bold text-white">Track</button>
      <button className="flex-1 bg-slate-100 rounded-lg py-1.5 text-[8px] font-bold text-slate-500">Share</button>
    </div>
    <div className="flex items-center gap-1 mt-3">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-orange-400 fill-orange-400" />)}
      <span className="text-[8px] text-slate-400 ml-1">Rate service</span>
    </div>
    <div className="mt-auto pt-3 w-full border-t border-slate-100 flex items-center justify-around">
      <Home className="w-3.5 h-3.5 text-slate-300" />
      <ShoppingBag className="w-3.5 h-3.5 text-blue-600" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const SCREEN_MAP = { dashboard: ScreenDashboard, tap: ScreenTap, form: ScreenForm, confirm: ScreenConfirm, success: ScreenSuccess };

// ── Cinematic Demo ───────────────────────────────────────────────────────────
const CinematicDemo = () => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('show');
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  const advance = useCallback(() => {
    setPhase('out');
    setTimeout(() => {
      setIdx(p => (p + 1) % SCENES.length);
      setPhase('in');
      setTimeout(() => setPhase('show'), TRANSITION_MS);
    }, TRANSITION_MS);
  }, []);

  useEffect(() => {
    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const pct = Math.min((ts - startRef.current) / SCENE_DURATION, 1);
      setProgress(pct);
      if (pct < 1) rafRef.current = requestAnimationFrame(tick);
      else { advance(); startRef.current = null; setProgress(0); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [idx, advance]);

  const scene = SCENES[idx];
  const Screen = SCREEN_MAP[scene.screen];

  const screenStyle = {
    opacity: phase === 'show' ? 1 : 0,
    transform: phase === 'out' ? 'translateY(10px) scale(0.98)' : phase === 'in' ? 'translateY(-6px) scale(1.01)' : 'none',
    transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
  };

  return (
    <div className="flex flex-col items-center select-none w-full">
      {/* Scene label */}
      <div className="flex items-center gap-3 mb-4" style={{ opacity: phase === 'show' ? 1 : 0, transition: `opacity ${TRANSITION_MS}ms ease` }}>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Live Demo</span>
        </div>
        <span className="text-slate-300">·</span>
        <span className="text-[10px] font-mono text-slate-400">{scene.chapter}</span>
      </div>

      {/* Scene title */}
      <div className="text-center mb-5" style={{ opacity: phase === 'show' ? 1 : 0, transform: phase === 'show' ? 'translateY(0)' : 'translateY(6px)', transition: `all ${TRANSITION_MS}ms ease` }}>
        <h3 className="text-xl font-black text-slate-800 mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{scene.title}</h3>
        <p className="text-sm text-slate-400">{scene.subtitle}</p>
      </div>

      {/* Phone */}
      <div className="relative">
        {/* Shadow glow */}
        <div className={`absolute inset-0 rounded-[40px] blur-2xl scale-105 transition-all duration-700 shadow-2xl ${scene.glow}`} style={{ opacity: 0.6 }} />

        {/* Phone shell */}
        <div className={`relative w-[200px] h-[410px] rounded-[36px] bg-white border-4 border-slate-200 overflow-hidden shadow-2xl ring-4 ${scene.ring} transition-all duration-500`}>
          {/* Dynamic island */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-4 bg-slate-900 rounded-2xl z-20 flex items-center justify-center gap-1">
            <div className="w-1 h-1 bg-slate-600 rounded-full" />
            <div className="w-1.5 h-1.5 bg-slate-700 rounded-full" />
          </div>

          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-200 z-30">
            <div className="h-full bg-blue-600 transition-none" style={{ width: `${progress * 100}%` }} />
          </div>

          <div className="w-full h-full pt-7" style={screenStyle}>
            <Screen />
          </div>
        </div>

        {/* Side buttons */}
        <div className="absolute right-[-3px] top-24 w-1 h-10 bg-slate-300 rounded-l-sm" />
        <div className="absolute left-[-3px] top-20 w-1 h-6 bg-slate-300 rounded-r-sm" />
        <div className="absolute left-[-3px] top-32 w-1 h-10 bg-slate-300 rounded-r-sm" />
      </div>

      {/* Dot timeline */}
      <div className="flex items-center gap-1.5 mt-5">
        {SCENES.map((s, i) => (
          <button key={s.id}
            onClick={() => { setIdx(i); startRef.current = null; setProgress(0); }}
            className={`transition-all duration-300 rounded-full ${i === idx ? 'w-6 h-2 bg-blue-600' : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'}`}
          />
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Book a pickup in under 60 seconds</p>
    </div>
  );
};

// ─── CUSTOMER AUTH ──────────────────────────────────────────────────────────
// ─── OTP SCREEN ────────────────────────────────────────────────────────────────
const OTPScreen = ({ email, customerId, onVerified, onResend, otpPurpose }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);
  const cooldownRef = useRef(null);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => { inputRefs.current[0]?.focus(); return () => clearInterval(cooldownRef.current); }, []);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setOtp(text.split(''));
      inputRefs.current[5]?.focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const endpoint = otpPurpose === 'pg' ? `${API}/customer/verify-otp` : `${API}/individual/verify-otp`;
      const r = await axios.post(endpoint, { customer_id: customerId, otp: code });
      onVerified(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendLoading(true);
    try {
      const endpoint = otpPurpose === 'pg' ? `${API}/customer/resend-otp` : `${API}/individual/resend-otp`;
      await axios.post(endpoint, { customer_id: customerId });
      toast.success('New OTP sent!');
      startCooldown();
      if (onResend) onResend();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend OTP'); }
    finally { setResendLoading(false); }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-5">
        <ShieldCheck className="w-7 h-7 text-blue-600" />
      </div>
      <h2 className="text-xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Verify your email</h2>
      <p className="text-sm text-slate-500 text-center mb-1">We sent a 6-digit code to</p>
      <p className="text-sm font-bold text-blue-600 mb-6">{email}</p>

      <div className="flex gap-2 mb-6" onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el}
            type="text" inputMode="numeric" maxLength={1} value={digit}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            className={`w-11 h-13 text-center text-xl font-black border-2 rounded-xl outline-none transition-all
              ${digit ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-slate-50 text-slate-800'}
              focus:border-blue-500 focus:bg-blue-50`}
            style={{ height: '52px' }}
          />
        ))}
      </div>

      <button onClick={handleVerify} disabled={loading || otp.join('').length !== 6}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all text-sm mb-4">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Verify & Continue</>}
      </button>

      <button onClick={handleResend} disabled={resendCooldown > 0 || resendLoading}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 disabled:opacity-50 transition-colors">
        {resendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
      </button>
    </div>
  );
};

// ─── CUSTOMER AUTH ──────────────────────────────────────────────────────────
const CustomerAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pgs, setPgs] = useState([]);
  const [activeTab, setActiveTab] = useState('login');
  const [customerType, setCustomerType] = useState(null);
  // phase: 'form' | 'otp'
  const [phase, setPhase] = useState('form');
  const [pendingCustomer, setPendingCustomer] = useState(null); // { id, email }
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [registerData, setRegisterData] = useState({ full_name: '', email: '', phone: '', password: '', address: '', pg_id: '' });

  useEffect(() => {
    const existing = localStorage.getItem('customerToken');
    if (existing) { navigate('/customer/dashboard'); return; }
    axios.get(`${API}/customer/pgs/active`).then(r => setPgs(r.data)).catch(() => {});
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await axios.post(`${API}/customer/login`, loginData);
      localStorage.setItem('customerToken', r.data.token);
      localStorage.setItem('customerData', JSON.stringify(r.data.customer));
      toast.success('Welcome back!');
      navigate('/customer/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Special case: email not verified — show OTP screen
      if (detail?.error === 'email_not_verified') {
        setPendingCustomer({ id: detail.customer_id, email: detail.email });
        setPhase('otp');
        toast('Please verify your email to continue', { icon: '📧' });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Login failed');
      }
    }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.pg_id) { toast.error('Please select your PG'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/customer/register`, registerData);
      setPendingCustomer({ id: r.data.customer_id, email: r.data.email });
      setPhase('otp');
      toast.success(`OTP sent to ${r.data.email}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const handleOTPVerified = (data) => {
    localStorage.setItem('customerToken', data.token);
    localStorage.setItem('customerData', JSON.stringify(data.customer));
    toast.success('Email verified! Welcome to JustFold!');
    navigate('/customer/dashboard');
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  // Show choice screen first
  if (!customerType) {
    return (
      <CustomerTypeChoice
        onSelectPG={() => setCustomerType('pg')}
        onSelectIndividual={() => navigate('/individual/login')}
        onBack={() => navigate('/')}
      />
    );
  }

  // OTP phase — replace right panel content only
  const renderOTPPanel = () => (
    <div className="flex flex-col flex-1 lg:max-w-md items-center justify-center px-6 py-12 lg:px-12 bg-white">
      <div className="w-full mb-6">
        <button onClick={() => setPhase('form')} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
      <OTPScreen
        email={pendingCustomer?.email}
        customerId={pendingCustomer?.id}
        otpPurpose="pg"
        onVerified={handleOTPVerified}
      />
      <p className="text-xs text-slate-300 text-center mt-8">Check your spam folder if you don't see the email.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── LEFT: Demo Panel ── */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden items-center justify-center px-8 py-16 bg-gradient-to-br from-blue-50 via-slate-50 to-violet-50">
        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-100 rounded-full blur-[80px] opacity-70" />
          <div className="absolute bottom-10 right-0 w-64 h-64 bg-orange-100 rounded-full blur-[70px] opacity-60" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-48 h-48 bg-violet-100 rounded-full blur-[60px] opacity-50" />
          <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(circle, #CBD5E1 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        </div>

        <div className="relative z-10 flex flex-col items-center max-w-sm w-full">
          <img src="/logo.png" alt="JustFold" className="h-10 mb-8 object-contain" />
          <CinematicDemo />

          {/* Testimonial */}
          <div className="mt-8 bg-white rounded-2xl p-4 shadow-sm border border-slate-100 w-full">
            <div className="flex gap-1 mb-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-orange-400 fill-orange-400" />)}
            </div>
            <p className="text-sm text-slate-600 leading-relaxed italic">"JustFold made my laundry routine effortless. Fresh clothes every week!"</p>
            <p className="text-xs text-slate-400 mt-2 font-medium">— Priya S., Sunrise PG Gurgaon</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px bg-slate-100 self-stretch" />

      {/* ── RIGHT: OTP phase or Auth Form ── */}
      {phase === 'otp' ? renderOTPPanel() : (
      <div className="flex flex-col flex-1 lg:max-w-md items-center justify-center px-6 py-12 lg:px-12 bg-white">

        {/* Back + heading */}
        <div className="w-full mb-8">
          <button onClick={() => setCustomerType(null)} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3 mb-1 lg:hidden">
            <img src="/logo.png" alt="JustFold" className="h-8 object-contain" />
          </div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {activeTab === 'login' ? 'Welcome back 👋' : 'Create account ✨'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {activeTab === 'login' ? 'Login to your laundry account' : 'Join JustFold — free to start'}
          </p>
        </div>

        {/* Tab toggle */}
        <div className="w-full flex bg-slate-100 rounded-2xl p-1 mb-8">
          {['login', 'register'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition-all ${
                activeTab === tab ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {tab === 'login' ? 'Login' : 'Register'}
            </button>
          ))}
        </div>

        {/* ── LOGIN ── */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="w-full space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="tel" placeholder="Enter your phone number" value={loginData.phone}
                  onChange={e => setLoginData({...loginData, phone: e.target.value})}
                  required data-testid="login-phone" className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="password" placeholder="Enter your password" value={loginData.password}
                  onChange={e => setLoginData({...loginData, password: e.target.value})}
                  required data-testid="login-password" className={inputClass} />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm mt-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Login to JustFold <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-slate-400 text-center">
              Don't have an account?{' '}
              <button type="button" onClick={() => setActiveTab('register')} className="text-blue-600 hover:text-blue-700 font-semibold">Register here</button>
            </p>
          </form>
        )}

        {/* ── REGISTER ── */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="w-full space-y-3.5">
            {[
              { label: 'Full Name', key: 'full_name', icon: User, type: 'text', placeholder: 'Enter your full name', testid: 'register-name' },
              { label: 'Gmail Address', key: 'email', icon: Mail, type: 'email', placeholder: 'your@gmail.com', testid: 'register-email', hint: 'Verification code will be sent here' },
              { label: 'Phone Number', key: 'phone', icon: Phone, type: 'tel', placeholder: 'Enter your phone number', testid: 'register-phone' },
              { label: 'Password', key: 'password', icon: Lock, type: 'password', placeholder: 'Create a password', testid: 'register-password' },
              { label: 'Address', key: 'address', icon: MapPin, type: 'text', placeholder: 'Enter your address', testid: 'register-address' },
            ].map(({ label, key, icon: Icon, type, placeholder, testid, hint }) => (
              <div key={key}>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
                <div className="relative">
                  <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type={type} placeholder={placeholder} value={registerData[key]}
                    onChange={e => setRegisterData({...registerData, [key]: e.target.value})}
                    required data-testid={testid} className={inputClass} />
                </div>
                {hint && <p className="text-[11px] text-slate-400 mt-1 ml-1">{hint}</p>}
              </div>
            ))}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">PG / Co-Living Space</label>
              <div className="relative">
                <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <Select value={registerData.pg_id} onValueChange={v => setRegisterData({...registerData, pg_id: v})}>
                  <SelectTrigger data-testid="register-pg"
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-700 hover:border-blue-300 focus:border-blue-500 rounded-xl h-12">
                    <SelectValue placeholder="Select your PG" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    {pgs.map(pg => (
                      <SelectItem key={pg.id} value={pg.id} className="hover:bg-slate-50 focus:bg-slate-50">
                        {pg.name} — {pg.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-orange-200 hover:-translate-y-0.5 text-sm mt-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <Zap className="w-4 h-4" /></>}
            </button>

            <p className="text-xs text-slate-400 text-center">
              Already have an account?{' '}
              <button type="button" onClick={() => setActiveTab('login')} className="text-blue-600 hover:text-blue-700 font-semibold">Login here</button>
            </p>
          </form>
        )}

        {/* Mobile teaser */}
        <div className="lg:hidden mt-8 w-full bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-xs text-slate-500">Book your first laundry pickup in under 60 seconds.</p>
        </div>

        <p className="text-xs text-slate-300 text-center mt-8">By continuing you agree to JustFold's terms of service.</p>
      </div>
      )}
    </div>
  );
};

export default CustomerAuth;
